'use client';
import { enUS } from 'date-fns/locale';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import type { BonanzaPromoDataApi } from '@/api/cms';
import { amountWithCurrencySymbol } from '@/helpers/currency';
import { showTooltip } from '@/helpers/tooltip';
import { useNavigation } from '@/hooks/navigation';
import { useCMSConfig } from '@/modules/common';
import { STORAGE, useGetCommonStore, useLocalStorageStore } from '@/store';
import { TooltipTypes } from '@/store/tooltipStore';
import { LOCALE_MAP } from '../components/BonanzaPromoPage/TopSection/ProgressBar';
import {
  BonanzaPromoStatus,
  checkIsBonanzaPromoLastRound,
  getBonanzaPromoBonus,
  getCurrentBonanzaIterationDays,
  updateBonanzaData,
} from '../helpers';

export const useBonanzaPromo = (data?: BonanzaPromoDataApi) => {
  const { navigate } = useNavigation();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const useCommonStore = useGetCommonStore();

  const {
    user,
    userBonanzaGroup,
    userBonanzaGroupKey,
    formatAmountByCurrency,
    bonuses,
    bonanzaPromoMenuData,
  } = useCommonStore(
    useShallow((state) => ({
      user: state.user,
      bonuses: state.bonuses,
      userBonanzaGroup: state.userBonanzaGroup,
      formatAmountByCurrency: state.formatAmountByCurrency,
      userBonanzaGroupKey: state.userBonanzaGroupKey,
      bonanzaPromoMenuData: state.bonanzaPromoMenuData,
    }))
  );

  const { cmsConfig } = useCMSConfig();

  const isLockedPromo = useMemo(
    () => userBonanzaGroup === BonanzaPromoStatus.PROMO_LOCKED,
    [userBonanzaGroup]
  );

  const isBonusIssued = useMemo(
    () => userBonanzaGroup === BonanzaPromoStatus.LEVEL_4,
    [userBonanzaGroup]
  );

  const isLvl3 = useMemo(
    () => userBonanzaGroup === BonanzaPromoStatus.LEVEL_3,
    [userBonanzaGroup]
  );

  const isLastRound = useMemo(
    () => checkIsBonanzaPromoLastRound(cmsConfig?.bonanzaPromo?.dateRange?.end),
    [cmsConfig?.bonanzaPromo?.dateRange?.end]
  );

  const isPromoCompleted = useMemo(() => {
    if (!(isLockedPromo && cmsConfig && cmsConfig.bonanzaPromo?.dateRange))
      return;

    return isLastRound;
  }, [cmsConfig, isLockedPromo, isLastRound]);

  const nextIterationStartDay = useMemo(() => {
    const startDate = cmsConfig?.bonanzaPromo?.dateRange?.start;
    if (!startDate || isLastRound) return null;

    const locale = LOCALE_MAP[language] || enUS;
    return getCurrentBonanzaIterationDays(startDate, locale).nextStartDayFull;
  }, [cmsConfig?.bonanzaPromo?.dateRange?.start, isLastRound, language]);

  const characterByStatus = useMemo(
    () =>
      bonanzaPromoMenuData?.menu.charactersByStatus.find(
        ({ promoStatus }) => promoStatus === userBonanzaGroupKey
      ),
    [bonanzaPromoMenuData, userBonanzaGroupKey]
  );

  const bgByStatus = useMemo(
    () =>
      data?.bgByStatus?.find(
        ({ promoStatus }) => promoStatus === userBonanzaGroupKey
      ),
    [userBonanzaGroupKey, data?.bgByStatus]
  );

  const handleAcknowledgeBonus = useCallback(() => {
    useLocalStorageStore.getState().push(STORAGE.BONUS_BONANZA_CLICK, true);
    updateBonanzaData(useCommonStore.setState, language, user);

    if (isLastRound) {
      showTooltip({
        type: TooltipTypes.INFO,
        body: (
          <div>
            <strong>{t('bonanzaPromo.lastRound.title')}</strong>
            <p>{t('bonanzaPromo.lastRound.description')}</p>
          </div>
        ),
      });
    }
  }, [t, language, user, useCommonStore.setState, isLastRound]);

  const bonanzaPromoBonus = useMemo(
    () => getBonanzaPromoBonus(bonuses),
    [bonuses]
  );

  const promoBonus = useMemo(() => {
    if (!bonanzaPromoBonus) return;

    const { currency, amountCents, amountWagerRequirementCents } =
      bonanzaPromoBonus;

    const amount = amountWithCurrencySymbol(
      bonanzaPromoBonus.currency,
      formatAmountByCurrency(amountCents, currency),
      { wholeRounding: true }
    );

    const wager = amountWagerRequirementCents / amountCents;

    return { amount, wager };
  }, [bonanzaPromoBonus, formatAmountByCurrency]);

  const handleNavigateToBonanza = useCallback(() => {
    navigate('/bonanza');
  }, [navigate]);

  return {
    isLastRound,
    isLvl3,
    isLockedPromo,
    isBonusIssued,
    isPromoCompleted,
    nextIterationStartDay,
    characterByStatus,
    bgByStatus,
    promoBonus,
    handleAcknowledgeBonus,
    handleNavigateToBonanza,
  };
};
